import React, { useState } from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(5px);
  z-index: 2000;
  align-items: center;
  justify-content: center;
  padding: 20px;
  animation: fadeIn 0.3s ease;
  overflow-y: auto;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  animation: slideUp 0.3s ease;
  margin: auto;
  
  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    max-width: 100%;
    max-height: 100vh;
    height: 100vh;
    border-radius: 0;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: transparent;
  border: none;
  font-size: 32px;
  color: #666;
  cursor: pointer;
  padding: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
  z-index: 10;
  
  &:hover {
    background: rgba(0, 0, 0, 0.05);
    color: #000;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    top: 15px;
    right: 15px;
    font-size: 28px;
    width: 36px;
    height: 36px;
  }
`;

const ModalHeader = styled.div`
  background: linear-gradient(135deg, #FFD700 0%, #ffed4e 100%);
  padding: 30px;
  border-radius: 16px 16px 0 0;
  text-align: center;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    border-radius: 0;
    padding: 25px 20px;
  }
`;

const Title = styled.h2`
  color: #232B3B;
  font-size: 26px;
  font-weight: 800;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 1px;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 22px;
  }
`;

const Subtitle = styled.p`
  color: #333;
  font-size: 15px;
  line-height: 1.5;
  opacity: 0.9;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 14px;
  }
`;

const FormContainer = styled.form`
  padding: 30px;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 20px;
  }
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 30px;
  gap: 8px;
  flex-wrap: wrap;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    margin-bottom: 25px;
    gap: 6px;
  }
`;

const Step = styled.div<{ active: boolean; completed: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => 
    props.completed ? 'linear-gradient(135deg, #10a37f 0%, #13c89f 100%)' :
    props.active ? 'linear-gradient(135deg, #FFD700 0%, #ffed4e 100%)' :
    '#e0e0e0'
  };
  color: ${props => (props.active || props.completed) ? '#fff' : '#999'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  transition: all 0.3s;
  box-shadow: ${props => props.active ? '0 4px 12px rgba(255, 215, 0, 0.3)' : 'none'};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 35px;
    height: 35px;
    font-size: 13px;
  }
`;

const StepConnector = styled.div<{ completed: boolean }>`
  width: 30px;
  height: 3px;
  background: ${props => props.completed ? '#10a37f' : '#e0e0e0'};
  transition: all 0.3s;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 20px;
    height: 2px;
  }
`;

const StepContent = styled.div<{ active: boolean }>`
  display: ${props => props.active ? 'block' : 'none'};
  animation: ${props => props.active ? 'fadeInStep 0.3s ease' : 'none'};
  
  @keyframes fadeInStep {
    from {
      opacity: 0;
      transform: translateX(10px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
`;

const StepTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #232B3B;
  margin-bottom: 20px;
  text-align: center;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 18px;
    margin-bottom: 15px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  color: #333;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 13px;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 14px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.2s;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #FFD700;
    box-shadow: 0 0 0 3px rgba(255, 215, 0, 0.1);
  }
  
  &::placeholder {
    color: #999;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 12px;
    font-size: 15px;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 14px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 16px;
  min-height: 120px;
  resize: vertical;
  font-family: inherit;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
    border-color: #FFD700;
    box-shadow: 0 0 0 3px rgba(255, 215, 0, 0.1);
  }
  
  &::placeholder {
    color: #999;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 12px;
    font-size: 15px;
    min-height: 100px;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 14px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.2s;
  background: white;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #FFD700;
    box-shadow: 0 0 0 3px rgba(255, 215, 0, 0.1);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 12px;
    font-size: 15px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 15px;
  justify-content: space-between;
  margin-top: 30px;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column-reverse;
    gap: 10px;
    margin-top: 25px;
  }
`;

const Button = styled.button<{ $primary?: boolean }>`
  flex: 1;
  background: ${props => props.$primary 
    ? 'linear-gradient(135deg, #FFD700 0%, #ffed4e 100%)'
    : 'transparent'
  };
  color: ${props => props.$primary ? '#232B3B' : '#666'};
  padding: 14px 24px;
  border: ${props => props.$primary ? 'none' : '2px solid #e0e0e0'};
  border-radius: 8px;
  font-size: 16px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${props => props.$primary 
      ? '0 6px 20px rgba(255, 215, 0, 0.4)'
      : '0 4px 12px rgba(0, 0, 0, 0.1)'
    };
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 12px 20px;
    font-size: 14px;
    width: 100%;
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: #f0f0f0;
  border-radius: 4px;
  margin-bottom: 25px;
  overflow: hidden;
`;

const Progress = styled.div<{ percent: number }>`
  width: ${props => props.percent}%;
  height: 100%;
  background: linear-gradient(90deg, #FFD700 0%, #ffed4e 100%);
  transition: width 0.3s ease;
`;

const HelpText = styled.p`
  font-size: 13px;
  color: #666;
  margin-top: 5px;
  font-style: italic;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 12px;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  padding: 12px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    border-color: #FFD700;
    background: rgba(255, 215, 0, 0.05);
  }
  
  input {
    margin-right: 12px;
    width: 20px;
    height: 20px;
    accent-color: #FFD700;
  }
  
  span {
    font-size: 15px;
    color: #333;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 10px;
    
    span {
      font-size: 14px;
    }
  }
`;

const SuccessMessage = styled.div`
  background: #e8f5e9;
  border: 2px solid #4caf50;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  
  h3 {
    color: #2e7d32;
    font-size: 24px;
    margin-bottom: 15px;
  }
  
  p {
    color: #2e7d32;
    font-size: 16px;
    line-height: 1.5;
  }
`;

interface DeepDiveModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const DeepDiveModal: React.FC<DeepDiveModalProps> = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    // Step 1: Contact Information
    name: '',
    email: '',
    phone: '',
    preferredContact: 'email',
    
    // Step 2: Product Information (matching QuickAnswer modal)
    companyName: '',  // Insurance company
    productName: '',  // Annuity product name
    yearPurchased: '',
    hasAnnuity: '',
    
    // Step 3: Analysis Needs
    analysisType: '',
    primaryConcern: '',
    specificQuestions: '',
    
    // Step 4: Financial Details (Optional)
    totalDeposited: '',
    currentValue: '',
    withdrawalNeeds: '',
    timeHorizon: ''
  });

  const totalSteps = 4;
  const progressPercent = (currentStep / totalSteps) * 100;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (currentStep < totalSteps) {
      handleNext();
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const emailData = {
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone,
        insurance_company: formData.companyName || 'Deep Dive Request',
        annuity_product: formData.productName || 'Multiple Products Analysis',
        year_purchased: formData.yearPurchased || 'N/A',
        current_age: 'N/A',
        initial_investment: formData.totalDeposited || 'N/A',
        primary_goal: 'Deep Dive Analysis',
        submission_type: 'deep_dive',
        message: `
DEEP DIVE ANALYSIS REQUEST
===========================

Contact Information:
Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone}
Preferred Contact: ${formData.preferredContact}

Product Information:
Company Name: ${formData.companyName}
Product Name: ${formData.productName}
Currently Owns: ${formData.hasAnnuity || 'Not specified'}
Year Purchased: ${formData.yearPurchased || 'N/A'}

Analysis Needs:
Analysis Type: ${formData.analysisType}
Primary Concern: ${formData.primaryConcern}
Specific Questions: ${formData.specificQuestions}

Financial Details:
Total Deposited: ${formData.totalDeposited || 'N/A'}
Current Value: ${formData.currentValue || 'N/A'}
Withdrawal Needs: ${formData.withdrawalNeeds || 'N/A'}
Time Horizon: ${formData.timeHorizon || 'N/A'}

Submitted: ${new Date().toLocaleString()}
        `.trim()
      };

      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ data: emailData }),
      });

      if (response.ok) {
        setSubmitSuccess(true);
        setTimeout(() => {
          onClose();
          setCurrentStep(1);
          setSubmitSuccess(false);
          setFormData({
            name: '',
            email: '',
            phone: '',
            preferredContact: 'email',
            companyName: '',
            productName: '',
            yearPurchased: '',
            hasAnnuity: '',
            analysisType: '',
            primaryConcern: '',
            specificQuestions: '',
            totalDeposited: '',
            currentValue: '',
            withdrawalNeeds: '',
            timeHorizon: ''
          });
        }, 3000);
      } else {
        alert('There was an error submitting your request. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const canProceedStep1 = formData.name && formData.email && formData.phone;
  const canProceedStep2 = formData.companyName && formData.productName;
  const canProceedStep3 = formData.analysisType && formData.primaryConcern;

  return (
    <ModalOverlay isOpen={isOpen} onClick={handleOverlayClick}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>×</CloseButton>
        
        <ModalHeader>
          <Title>🔍 Deep Dive Analysis</Title>
          <Subtitle>
            Get comprehensive analysis of multiple annuity products with real data
          </Subtitle>
        </ModalHeader>
        
        <FormContainer onSubmit={handleSubmit}>
          {!submitSuccess ? (
            <>
              <ProgressBar>
                <Progress percent={progressPercent} />
              </ProgressBar>
              
              <StepIndicator>
                {[1, 2, 3, 4].map((step, index) => (
                  <React.Fragment key={step}>
                    <Step 
                      active={currentStep === step}
                      completed={currentStep > step}
                    >
                      {currentStep > step ? '✓' : step}
                    </Step>
                    {index < 3 && (
                      <StepConnector completed={currentStep > step} />
                    )}
                  </React.Fragment>
                ))}
              </StepIndicator>

              {/* Step 1: Contact Information */}
              <StepContent active={currentStep === 1}>
                <StepTitle>Let's Get Started</StepTitle>
                
                <FormGroup>
                  <Label htmlFor="name">Your Name *</Label>
                  <Input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Smith"
                    required
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    required
                  />
                  <HelpText>We'll send your detailed analysis here</HelpText>
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(555) 123-4567"
                    required
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label>Preferred Contact Method</Label>
                  <RadioGroup>
                    <RadioLabel>
                      <input
                        type="radio"
                        name="preferredContact"
                        value="email"
                        checked={formData.preferredContact === 'email'}
                        onChange={handleChange}
                      />
                      <span>Email</span>
                    </RadioLabel>
                    <RadioLabel>
                      <input
                        type="radio"
                        name="preferredContact"
                        value="phone"
                        checked={formData.preferredContact === 'phone'}
                        onChange={handleChange}
                      />
                      <span>Phone</span>
                    </RadioLabel>
                    <RadioLabel>
                      <input
                        type="radio"
                        name="preferredContact"
                        value="both"
                        checked={formData.preferredContact === 'both'}
                        onChange={handleChange}
                      />
                      <span>Either is fine</span>
                    </RadioLabel>
                  </RadioGroup>
                </FormGroup>
              </StepContent>

              {/* Step 2: Product Information */}
              <StepContent active={currentStep === 2}>
                <StepTitle>Product Information</StepTitle>
                
                <FormGroup>
                  <Label htmlFor="companyName">Insurance Company Name *</Label>
                  <Input
                    type="text"
                    id="companyName"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    placeholder="e.g., Allianz, American Equity"
                    required
                  />
                  <HelpText>Enter the insurance company for the annuity you want analyzed</HelpText>
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="productName">Annuity Product Name *</Label>
                  <Input
                    type="text"
                    id="productName"
                    name="productName"
                    value={formData.productName}
                    onChange={handleChange}
                    placeholder="e.g., Core Income 7, 222 Annuity"
                    required
                  />
                  <HelpText>Enter the specific product name</HelpText>
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="hasAnnuity">Do you currently own this annuity?</Label>
                  <Select
                    id="hasAnnuity"
                    name="hasAnnuity"
                    value={formData.hasAnnuity}
                    onChange={handleChange}
                  >
                    <option value="">Please select...</option>
                    <option value="yes-own">Yes, I own this annuity</option>
                    <option value="yes-multiple">Yes, plus I have other annuities</option>
                    <option value="no-shopping">No, I'm researching before buying</option>
                    <option value="agent">I'm an agent researching for clients</option>
                  </Select>
                </FormGroup>
                
                {(formData.hasAnnuity === 'yes-own' || formData.hasAnnuity === 'yes-multiple') && (
                  <FormGroup>
                    <Label htmlFor="yearPurchased">When did you purchase it?</Label>
                    <Select
                      id="yearPurchased"
                      name="yearPurchased"
                      value={formData.yearPurchased}
                      onChange={handleChange}
                    >
                      <option value="">Select year...</option>
                      <option value="2024">2024</option>
                      <option value="2023">2023</option>
                      <option value="2022">2022</option>
                      <option value="2021">2021</option>
                      <option value="2020">2020</option>
                      <option value="2015-2019">2015-2019</option>
                      <option value="2010-2014">2010-2014</option>
                      <option value="before-2010">Before 2010</option>
                    </Select>
                  </FormGroup>
                )}
              </StepContent>

              {/* Step 3: Analysis Needs */}
              <StepContent active={currentStep === 3}>
                <StepTitle>What Would You Like to Know?</StepTitle>
                
                <FormGroup>
                  <Label htmlFor="analysisType">Type of Analysis Needed *</Label>
                  <Select
                    id="analysisType"
                    name="analysisType"
                    value={formData.analysisType}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select analysis type...</option>
                    <option value="performance">Performance Review</option>
                    <option value="comparison">Product Comparison</option>
                    <option value="replacement">1035 Exchange Analysis</option>
                    <option value="optimization">Strategy Optimization</option>
                    <option value="multiple">Multiple Products Review</option>
                    <option value="comprehensive">Everything - Full Analysis</option>
                  </Select>
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="primaryConcern">Primary Concern or Goal *</Label>
                  <Select
                    id="primaryConcern"
                    name="primaryConcern"
                    value={formData.primaryConcern}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select your main concern...</option>
                    <option value="returns">Maximize Returns</option>
                    <option value="income">Generate Income</option>
                    <option value="safety">Protect Principal</option>
                    <option value="fees">Understand Fees</option>
                    <option value="surrender">Exit Strategy</option>
                    <option value="beneficiary">Legacy Planning</option>
                    <option value="other">Other Concerns</option>
                  </Select>
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="specificQuestions">Specific Questions or Concerns</Label>
                  <TextArea
                    id="specificQuestions"
                    name="specificQuestions"
                    value={formData.specificQuestions}
                    onChange={handleChange}
                    placeholder="Tell us what specific information would be most helpful for you..."
                  />
                  <HelpText>The more detail you provide, the better we can help</HelpText>
                </FormGroup>
              </StepContent>

              {/* Step 4: Financial Details (Optional) */}
              <StepContent active={currentStep === 4}>
                <StepTitle>Optional: Financial Details</StepTitle>
                <p style={{ textAlign: 'center', marginBottom: '25px', color: '#666', fontSize: '15px' }}>
                  These details help us provide more accurate analysis, but they're optional
                </p>
                
                <FormGroup>
                  <Label htmlFor="totalDeposited">Total Amount Invested</Label>
                  <Input
                    type="text"
                    id="totalDeposited"
                    name="totalDeposited"
                    value={formData.totalDeposited}
                    onChange={handleChange}
                    placeholder="e.g., $100,000"
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="currentValue">Current Account Value</Label>
                  <Input
                    type="text"
                    id="currentValue"
                    name="currentValue"
                    value={formData.currentValue}
                    onChange={handleChange}
                    placeholder="e.g., $125,000"
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="withdrawalNeeds">Income/Withdrawal Needs</Label>
                  <Select
                    id="withdrawalNeeds"
                    name="withdrawalNeeds"
                    value={formData.withdrawalNeeds}
                    onChange={handleChange}
                  >
                    <option value="">Select if applicable...</option>
                    <option value="none">No withdrawals needed</option>
                    <option value="immediate">Need income now</option>
                    <option value="1-3years">Within 1-3 years</option>
                    <option value="3-5years">Within 3-5 years</option>
                    <option value="5-10years">Within 5-10 years</option>
                    <option value="10plus">10+ years from now</option>
                  </Select>
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="timeHorizon">Investment Time Horizon</Label>
                  <Select
                    id="timeHorizon"
                    name="timeHorizon"
                    value={formData.timeHorizon}
                    onChange={handleChange}
                  >
                    <option value="">Select time frame...</option>
                    <option value="short">Less than 5 years</option>
                    <option value="medium">5-10 years</option>
                    <option value="long">10-20 years</option>
                    <option value="legacy">20+ years / Legacy</option>
                  </Select>
                </FormGroup>
              </StepContent>

              <ButtonGroup>
                {currentStep > 1 && (
                  <Button type="button" onClick={handlePrev}>
                    ← Previous
                  </Button>
                )}
                {currentStep < totalSteps ? (
                  <Button 
                    type="button" 
                    $primary 
                    onClick={handleNext}
                    disabled={
                      (currentStep === 1 && !canProceedStep1) ||
                      (currentStep === 2 && !canProceedStep2) ||
                      (currentStep === 3 && !canProceedStep3)
                    }
                  >
                    Next Step →
                  </Button>
                ) : (
                  <Button type="submit" $primary disabled={isSubmitting}>
                    {isSubmitting ? 'Submitting...' : 'Submit Request'}
                  </Button>
                )}
              </ButtonGroup>
            </>
          ) : (
            <SuccessMessage>
              <h3>✅ Request Received!</h3>
              <p>
                Thank you for your Deep Dive request!<br/>
                Our team will analyze your information and contact you within 24 hours
                with a comprehensive report tailored to your specific needs.
              </p>
            </SuccessMessage>
          )}
        </FormContainer>
      </ModalContent>
    </ModalOverlay>
  );
};